import {AuthService} from 'compob2b-auth';
import {environment} from '../environments/environment';
import {ApiResponse, InitData, StoreService} from 'compob2b-libs';
import {Injector} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {showCaseRoutes} from './cabinet/showcase.routing.module';

/**
 * Showcase-вариант app initializer'а. Подменяет основной
 * `COMPO_APP_INITIALIZER.ts` в showcase-сборке через fileReplacement
 * в angular.json `nextshowcase` config.
 *
 * Зачем: cabinet's `routes` не нужны в showcase bundle. Также не нужна
 * module-фильтрация (showcase routes минимальны).
 */
/**
 * Однократный self-reload при ChunkLoadError на витрине.
 *
 * Витрина — CSR-статик без Service Worker (SW выпилен, см. environment.showcase.ts
 * и docs/showcase-build-topology.md «Кэш и доставка статики»). Открытая вкладка со
 * СТАРЫМ index.html после деплоя лениво тянет чанк по устаревшему content-hash →
 * 404 → ChunkLoadError. Ловим глобально и один раз перезагружаем страницу (свежий
 * index → свежие хэши). Гард в sessionStorage не даёт зациклиться, если чанк пропал
 * навсегда; через RE_ARM_MS гард снимается, чтобы следующий деплой снова мог
 * перезагрузить.
 */
const CHUNK_RELOAD_GUARD_KEY = 'showcase.chunkReload';
const CHUNK_RELOAD_REARM_MS = 15_000;

function isChunkLoadError(reason: unknown): boolean {
  const err = reason as {name?: string; message?: string} | string | undefined | null;
  const text = typeof err === 'string' ? err : `${err?.name ?? ''} ${err?.message ?? ''}`;
  return /ChunkLoadError|Loading chunk [^ ]+ failed|(error|failed to fetch) .*dynamically imported module|importing a module script failed/i.test(text);
}

function reloadOnceForChunkError(reason: unknown): void {
  if (!isChunkLoadError(reason)) {
    return;
  }
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)) {
      return; // уже перезагружались из-за чанка — не зацикливаемся
    }
    sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
  } catch {
    // sessionStorage недоступен (private mode и т.п.) — деградируем в single reload
  }
  window.location.reload();
}

function installChunkReloadGuard(): void {
  window.addEventListener('unhandledrejection', (e) => reloadOnceForChunkError(e.reason));
  window.addEventListener('error', (e) => reloadOnceForChunkError(e.error ?? e.message));
  // Приложение проработало RE_ARM_MS без нового ChunkLoadError → guard'ленная
  // перезагрузка удалась, снимаем гард, чтобы следующий деплой снова мог перезагрузить.
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    } catch {
      // ignore
    }
  }, CHUNK_RELOAD_REARM_MS);
}

// Сигнатура совпадает с cabinet-вариантом — app.module.ts регистрирует
// одинаковые `deps` для обоих сборок. Showcase — pure CSR, на SSR не идёт,
// поэтому storeService здесь не используется (но в deps есть).
export function init(
  auth: AuthService,
  platformId: object,
  _storeService: StoreService,
): () => Promise<ApiResponse<InitData> | Error> {
  return () => {
    if (!isPlatformBrowser(platformId)) {
      return Promise.resolve(undefined as any);
    }
    installChunkReloadGuard();
    if (environment.isOpenCabinet === true) {
      if (auth.isCustomer() || auth.isManager()) {
        return firstValueFrom(auth.initCustomer());
      }

      const isAnonymous = auth.checkAndSetAnonymousToken();
      if (isAnonymous !== null) {
        return firstValueFrom(isAnonymous);
      }
    }
  };
}

export function loadRoutes(injector: Injector) {
  return () => {
    const router: Router = injector.get(Router);
    router.resetConfig(showCaseRoutes);
  };
}

// Showcase — pure CSR static: `/seo/config` витрина не запрашивает вовсе (SEO-модуль
// здесь выключен намеренно), поэтому сцеплять инициализаторы, как в cabinet-версии,
// нечего. Лишние аргументы, которые DI отдаёт по общим `deps`, игнорируются.
export {init as initApp};
